/* Plans — admin page to create access tiers and assign them to users.
   No payment gateway. Purely access-control with usage limits. */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Crown, Plus, Trash2, Check, Users as UsersIcon, Edit3 } from "lucide-react";
import { toast } from "sonner";

export default function Plans() {
  const { user } = useAuth();
  const isAdmin = ["super_admin", "company_admin"].includes(user?.role);
  const [plans, setPlans] = useState([]);
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const refresh = async () => {
    const [pl, us] = await Promise.all([
      api.get("/plans"),
      isAdmin ? api.get("/users").catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
    ]);
    setPlans(pl.data || []);
    setUsers(us.data || []);
  };
  useEffect(() => { refresh(); }, []);

  const assign = async (userId, planId) => {
    try {
      await api.post(`/plans/assign/${userId}`, { plan_id: planId === "__clear__" ? null : planId });
      toast.success("Plan updated");
      refresh();
    } catch { toast.error("Failed to assign"); }
  };

  const del = async (pid) => {
    if (!window.confirm("Delete this plan?")) return;
    try { await api.delete(`/plans/${pid}`); toast.success("Plan deleted"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="plans-page">
      <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.24em] text-muted-foreground font-semibold flex items-center gap-2"><Crown className="size-3.5" /> SaaS access tiers</div>
          <h1 className="display text-3xl md:text-4xl font-black tracking-tighter mt-1">Plans & quotas</h1>
          <p className="text-muted-foreground mt-1 text-sm">Define how many active candidates each recruiter can manage. No payment — just access control.</p>
        </div>
        {isAdmin && <Button onClick={() => setCreating(true)} className="gap-2 lift" data-testid="plan-new"><Plus className="size-4" /> New plan</Button>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {plans.map((p) => (
          <Card key={p.id} className="border-border lift" data-testid={`plan-${p.id}`}>
            <CardContent className="p-5 relative overflow-hidden">
              <div className="absolute -top-8 -right-8 size-24 rounded-full opacity-15" style={{ background: p.color }} />
              <div className="relative">
                <div className="flex items-center gap-2">
                  <div className="size-8 rounded-md grid place-items-center text-white font-bold" style={{ background: p.color }}><Crown className="size-4" /></div>
                  <div>
                    <div className="display font-bold text-xl">{p.name}</div>
                    {p.is_default && <span className="chip bg-emerald-500/15 text-emerald-700 text-[9px] uppercase tracking-wider font-bold">Default</span>}
                  </div>
                </div>
                <div className="mt-3 display text-4xl font-black tracking-tight" style={{ color: p.color }}>
                  {p.max_resumes === 0 ? "∞" : p.max_resumes}
                  <span className="text-xs text-muted-foreground font-normal ml-1">active candidates</span>
                </div>
                {p.description && <p className="text-xs text-muted-foreground mt-2">{p.description}</p>}
                {isAdmin && (
                  <div className="flex gap-1.5 mt-4">
                    <Button size="sm" variant="outline" onClick={() => setEditing(p)} className="gap-1" data-testid={`plan-edit-${p.id}`}><Edit3 className="size-3.5" /> Edit</Button>
                    <Button size="sm" variant="ghost" className="text-destructive gap-1" onClick={() => del(p.id)} disabled={p.is_default} data-testid={`plan-del-${p.id}`}><Trash2 className="size-3.5" /></Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {isAdmin && (
        <Card className="border-border">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <UsersIcon className="size-4 text-primary" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">User assignments</div>
                <div className="display font-bold text-lg">Assign plans to recruiters</div>
              </div>
            </div>
            <div className="space-y-2" data-testid="plan-user-list">
              {users.filter((u) => u.role !== "super_admin").map((u) => (
                <div key={u.id} className="flex items-center justify-between p-3 rounded-md border border-border" data-testid={`plan-user-${u.id}`}>
                  <div className="min-w-0">
                    <div className="font-semibold text-sm">{u.full_name}</div>
                    <div className="text-[11px] text-muted-foreground">{u.email} · {u.role.replace("_", " ")}</div>
                  </div>
                  <Select value={u.plan_id || "__clear__"} onValueChange={(v) => assign(u.id, v)}>
                    <SelectTrigger className="w-[200px] h-9" data-testid={`plan-user-select-${u.id}`}><SelectValue placeholder="No plan" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__clear__">— No plan (default) —</SelectItem>
                      {plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.max_resumes === 0 ? "Unlimited" : `${p.max_resumes} candidates`}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
              {users.filter((u) => u.role !== "super_admin").length === 0 && (
                <div className="text-sm text-muted-foreground py-4 text-center">No users yet.</div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {(editing || creating) && (
        <PlanDialog plan={editing} creating={creating} onClose={() => { setEditing(null); setCreating(false); refresh(); }} />
      )}
    </div>
  );
}

function PlanDialog({ plan, creating, onClose }) {
  const [p, setP] = useState(plan || { name: "", description: "", max_resumes: 50, color: "#1E5BFF", is_default: false });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!p.name) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      if (creating) await api.post("/plans", p);
      else await api.put(`/plans/${plan.id}`, p);
      toast.success(creating ? "Plan created" : "Plan updated");
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[480px]" data-testid="plan-dialog">
        <DialogHeader><DialogTitle>{creating ? "New plan" : "Edit plan"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input value={p.name} onChange={(e) => setP({ ...p, name: e.target.value })} data-testid="plan-input-name" /></div>
          <div><Label>Description</Label><Textarea rows={2} value={p.description || ""} onChange={(e) => setP({ ...p, description: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Max active candidates (0 = unlimited)</Label><Input type="number" min={0} value={p.max_resumes} onChange={(e) => setP({ ...p, max_resumes: Number(e.target.value) })} data-testid="plan-input-max" /></div>
            <div><Label>Badge colour</Label><Input type="color" value={p.color || "#1E5BFF"} onChange={(e) => setP({ ...p, color: e.target.value })} className="h-9 p-1" /></div>
          </div>
          <div className="flex items-center justify-between p-2.5 rounded-md border border-border">
            <div>
              <Label className="text-sm">Make this the default plan</Label>
              <p className="text-[11px] text-muted-foreground mt-0.5">New users will get this plan automatically.</p>
            </div>
            <Switch checked={!!p.is_default} onCheckedChange={(v) => setP({ ...p, is_default: v })} data-testid="plan-input-default" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="plan-save"><Check className="size-4 mr-1" /> {saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
